import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import dayjs from "dayjs";

import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, Stack, Tooltip, Typography, CircularProgress, Button, Chip, Grid } from "@mui/material";
import {
  Close,
  AccessTime,
  EventAvailable,
  OpenInNew,
  Checklist,
  ChatBubbleOutline,
  LocalHospital,
  VerifiedUser,
  Healing,
  Warning,
  CalendarMonth,
  Person,
  Schedule,
  AttachFile,
  Launch,
  Refresh,
  Tag,
  Lock,
  Edit,
  Public,
  Euro,
  PictureAsPdf,
} from "@mui/icons-material";

// Intentionally NOT importing ActionItem, EventEditModal, imageUrl (per your instruction)
// import { ActionItem } from "...";
// import EventEditModal, { EventRef } from "...";
// import { imageUrl } from "...";

import { ActionItem } from "@/shared/sharedService/ActionItem";

import EventEditModal from "./EventEditModal";
import { ChevronsDown, ChevronsUp, CircleDot, Delete, Redo, Undo } from "lucide-react";
import { BobEvent, BobEventMessage, imageUrl } from "./helpers";

import { captureTicket, TicketSnapshot, TicketExportResult } from "./ticketSnapshot";

type Props = {
  id: number;
  apiService: any;
  noHours: boolean;
  fpmMode: boolean;
  deviceNumber: number;
  modalMode?: boolean;
  fullAppRights: boolean;
  dateOnlyScheduling?: boolean;
  allowMessageDeletion?: boolean;
  role: "site" | "service";
  onClose: () => void;
  onCreate: (id: number) => void;
  onExportPdf?: (snapshot: TicketSnapshot) => Promise<TicketExportResult>;
};

function getCategoryIcon(category: BobEvent["category"]) {
  switch (category) {
    case "external":
      return OpenInNew;
    case "meterReading":
      return AccessTime;
    case "testing":
      return Checklist;
    case "maintenance":
      return VerifiedUser;
    case "damage":
      return Healing;
    default:
      return OpenInNew;
  }
}

function getBucketIcon(event: BobEvent) {
  if (!!event.completedAt) return EventAvailable;
  // keep your current mapping semantics from the grid version
  if (!!event.dueAt && dayjs(event.dueAt, "YYYY-MM-DD") <= dayjs()) return Warning;
  return AccessTime;
}

function getSubtitle(event: BobEvent, dateOnlyScheduling = false) {
  if (!!event.completedAt) {
    return "Erledigt am " + dayjs(event.completedAt).format("ddd, DD.MM.YYYY");
  }

  let duePart = "";
  if (!!event.dueAtAmount) {
    duePart = "Fällig bei " + event.dueAtAmount + " h";
    if (!!event.dueAt) {
      duePart += " (ca. " + dayjs(event.dueAt, "YYYY-MM-DD").fromNow() + ")";
    }
  } else if (!!event.dueAt) {
    duePart = "Fällig am " + dayjs(event.dueAt, "YYYY-MM-DD").format("ddd, DD.MM.YYYY");
  }

  if (!!event.scheduledDate || !!event.scheduledAt) {
    const date = event.scheduledDate ? dayjs(event.scheduledDate)
      : dateOnlyScheduling ? dayjs.utc(event.scheduledAt).local() : dayjs(event.scheduledAt);
    return "Geplant für " + date.format("ddd, DD.MM.YYYY") + (event.scheduledDate ? " (ohne Uhrzeit)" : "") + (duePart.length > 0 ? " - " : "") + duePart;
  } else if (duePart.length > 0) {
    return duePart;
  }

  return "Erstellt am " + dayjs(event.createdAt, "YYYY-MM-DD").format("ddd, DD.MM.YYYY");
}

export default function EventSidebar({ id, onClose, onCreate, fpmMode = false, noHours, modalMode, apiService, role, fullAppRights, dateOnlyScheduling = false, allowMessageDeletion = false, onExportPdf }: Props) {
  // Event

  /*const rights = useRights();

  const role = "SERVICE" in rights ? "service" : "site";
  const fullAppRights = "DISPOSITION" in rights || "SERVICE" in rights;*/

  const eventQuery = useQuery<BobEvent, any, BobEvent>({
    queryKey: ["getBobEvent", id],
    queryFn: apiService.getEntity("bobEvent", id, true, "resource"),
    enabled: id > 0,
  });

  const deviceNumber = eventQuery?.data?.resourceNumber;

  const deviceQuery = useQuery<any[], any, any | null>({
    queryKey: ["getDeviceByNumber", deviceNumber],
    enabled: !!deviceNumber,
    queryFn: apiService.getEntitiesWithFilter("resource", { number: deviceNumber }, "details"),
    select: (response) => (!!response && response.length > 0 ? response[0] : null),
  });

  // Messages
  const messageQuery = useQuery<BobEventMessage[]>({
    queryKey: ["getBobEventMessages", String(id)],
    queryFn: apiService.getEntitiesWithFilter("bobEventMessage", { event: ["bobEvent", id] }),
    enabled: id > 0,
  });

  const queryClient = useQueryClient();
  const canDeleteMessage = allowMessageDeletion && !fpmMode;
  type DeleteRequest = { messageId: number; eventId: number; deviceNumber: string };
  const [deleteTarget, setDeleteTarget] = useState<(DeleteRequest & { preview: string }) | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const activeEventId = useRef(id);
  activeEventId.current = id;
  const deletionInFlight = useRef(false);

  useEffect(() => { setDeleteTarget(null); setDeleteError(null); }, [id, canDeleteMessage]);

  const messageDeleteMutation = useMutation({
    mutationFn: (request: DeleteRequest) => apiService.deleteEntity("bobEventMessage", request.messageId)(request.messageId),
    onSuccess: async (_, request) => {
      await Promise.all([
        ["getBobEventMessages", String(request.eventId)], ["getBobEvent", request.eventId],
        ["getDeviceEvents", request.deviceNumber], ["getEvents"], ["getServiceByUser"],
      ].map(queryKey => queryClient.invalidateQueries({ queryKey })));
      if (activeEventId.current === request.eventId) { setDeleteTarget(null); setDeleteError(null); }
    },
    onError: (_, request) => {
      if (activeEventId.current === request.eventId) setDeleteError("Kommentar konnte nicht gelöscht werden. Bitte erneut versuchen.");
    },
    onSettled: () => { deletionInFlight.current = false; },
  });

  const messageLockMutation = useMutation({
    mutationFn: apiService.setMessageLock?.() ?? (async () => Promise.resolve()),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["getBobEventMessages", String(id)] });
    },
  });

  // If your EventEditModal needs a ref like in RN, keep it. VSCode will auto-import types.
  const modalRef = useRef<any>(null);

  const ticketRef = useRef<HTMLDivElement>(null);
  const currentId = useRef(id);
  currentId.current = id;
  const [exporting, setExporting] = useState(false);
  const [exportNotice, setExportNotice] = useState<{ error: boolean; text: string } | null>(null);
  useEffect(() => { setExportNotice(null); }, [id]);
  const exportPdf = async () => {
    if (!onExportPdf || !ticketRef.current || !eventQuery.data || !messageQuery.isSuccess) return;
    const snapshot = captureTicket(ticketRef.current, id, eventQuery.data.title || eventQuery.data.categorySettings?.title || "Werkstattticket");
    setExporting(true); setExportNotice(null);
    try {
      const result = await onExportPdf(snapshot);
      if (currentId.current === snapshot.id && result.warnings.length) setExportNotice({ error: false, text: "PDF unvollständig: " + result.warnings.join("; ") });
    } catch {
      if (currentId.current === snapshot.id) setExportNotice({ error: true, text: "PDF konnte nicht erstellt werden. Bitte erneut versuchen." });
    } finally { setExporting(false); }
  };

  const event = eventQuery.data;

  const headerIcon = useMemo(() => {
    if (!event) return null;
    const Icon = getCategoryIcon(event.category);
    return <Icon fontSize="small" />;
  }, [event]);

  console.log("CURRO", !!event?.resource, event?.resource, event, modalRef.current);

  return (
    <Box ref={ticketRef} sx={{ height: "100%", display: "flex", flexDirection: "column", pt: modalMode ? 0 : "50px" }}>
      {/* Header */}
      <Box sx={{ p: modalMode ? 0 : 2, display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack data-ticket-label={event ? "Priorität: " + ({ high: "Hoch", normal: "Normal", low: "Niedrig" }[event.priority]) : undefined} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            {event?.priority === "high" ? <ChevronsUp color="red" /> : event?.priority === "low" ? <ChevronsDown color="blue" /> : null}
            {headerIcon}
            <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
              {event?.title || event?.categorySettings?.title || "Service"}
            </Typography>
          </Stack>

          {event ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              {(() => {
                const Icon = getBucketIcon(event);
                return <Icon fontSize="small" />;
              })()}
              <Typography variant="body2" noWrap sx={{ color: "text.secondary", minWidth: 0 }}>
                {getSubtitle(event, dateOnlyScheduling)}
              </Typography>
            </Stack>
          ) : null}

          {event?.assignedTo && event.assignedTo !== "none" ? (
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Zuständig: {event.assignedTo === "service" ? "Werkstatt" : "Baustelle"}
            </Typography>
          ) : null}
        </Box>

        {onExportPdf ? <Button data-ticket-controls variant="outlined" size="small" startIcon={exporting ? <CircularProgress size={16} /> : <PictureAsPdf />}
          disabled={exporting || !eventQuery.isSuccess || eventQuery.isFetching || !messageQuery.isSuccess || messageQuery.isFetching}
          onClick={exportPdf}>PDF exportieren</Button> : null}
        {modalMode ? null : (
          <IconButton onClick={onClose} size="small" aria-label="Schließen">
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>

      {onExportPdf && exportNotice ? <Alert data-ticket-controls severity={exportNotice.error ? "error" : "warning"}>{exportNotice.text}</Alert> : null}
      {onExportPdf && messageQuery.isError ? <Alert data-ticket-controls severity="error">Nachrichten konnten nicht geladen werden. Der PDF-Export ist gesperrt.<Button onClick={() => messageQuery.refetch()}>Erneut laden</Button></Alert> : null}
      <Divider />

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: modalMode ? 0 : 2 }}>
        {eventQuery.isLoading || !event ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Lädt...
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {/* Resource summary (if present on event.resource) */}
            {event.resource ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                    Gerät
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
                    <span className="small_number">{event.resource.number}</span>
                    <Typography variant="body1" noWrap sx={{ minWidth: 0 }}>
                      {event.resource.name}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            ) : null}

            {!!deviceQuery.data ? (
              <EventEditModal
                dateOnlyScheduling={dateOnlyScheduling}
                ApiService={apiService}
                role={role}
                isFPM={fpmMode}
                noHours={noHours}
                onCreate={onCreate}
                ref={modalRef}
                testingContent={
                  deviceQuery.data.testingContent || deviceQuery.data.primaryResourceTag.testingContent
                    ? (deviceQuery.data.testingContent ?? []).concat(deviceQuery.data.primaryResourceTag.testingContent ?? [])
                    : null
                }
                lastReading={deviceQuery.data.lastMeterReading?.runningHours || null}
                deviceId={deviceQuery.data.id}
                deviceNumber={deviceQuery.data.number}
              />
            ) : null}

            {/* Actions (use ActionItem exactly as in your project; no import here) */}
            <Box data-ticket-controls sx={{ overflowX: "auto" }}>
              <Stack direction="row" spacing={1.5} sx={{ py: 1, minWidth: "max-content" }}>
                {event.category === "testing" && !event.completedAt ? (
                  <ActionItem
                    icon={Checklist}
                    color="primary"
                    label="Wartung starten"
                    onPress={() => modalRef.current?.openModal(event, "testing")}
                  />
                ) : null}

                {event.category === "meterReading" && !event.completedAt ? (
                  <ActionItem
                    icon={AccessTime}
                    color="primary"
                    label="Wert erfassen"
                    onPress={() => modalRef.current?.openModal(event, "meterReading")}
                  />
                ) : null}

                <ActionItem icon={ChatBubbleOutline} label="Kommentar" onPress={() => modalRef.current?.openModal(event, "note")} />

                {event.category === "damage" && fpmMode && !event.completedAt ? (
                  <ActionItem icon={Euro} color={undefined} label="Regulierung" onPress={() => modalRef.current?.openModal(event, "damages")} />
                ) : null}
                {!event.completedAt && fullAppRights ? (
                  <ActionItem
                    icon={event.priority === "high" ? ChevronsUp : event.priority === "low" ? ChevronsDown : CircleDot}
                    color={event.priority === "high" ? "red" : event.priority === "low" ? "blue" : undefined}
                    label="Priorität"
                    onPress={() => modalRef.current?.openModal(event, "priority")}
                  />
                ) : null}

                {event.category !== "testing" &&
                fullAppRights &&
                event.assignedTo === role &&
                event.category !== "meterReading" &&
                !!event.completedAt ? (
                  <ActionItem icon={Undo} label="Erneut öffnen" onPress={() => modalRef.current?.openModal(event, "reopen")} />
                ) : null}

                {event.category !== "testing" &&
                fullAppRights &&
                event.assignedTo === role &&
                event.category !== "meterReading" &&
                !event.completedAt ? (
                  <ActionItem icon={EventAvailable} label="Erledigt" onPress={() => modalRef.current?.openModal(event, "complete")} />
                ) : null}

                {!event.completedAt && fullAppRights && event.assignedTo === role ? (
                  <ActionItem
                    icon={Schedule}
                    color={!!event.scheduledAt || !!event.scheduledDate ? "blue" : undefined}
                    label="Termin planen"
                    onPress={() => modalRef.current?.openModal(event, "scheduledDate")}
                  />
                ) : null}

                {!event.completedAt && fullAppRights ? (
                  <Fragment>
                    <ActionItem icon={Tag} label="Titel" onPress={() => modalRef.current?.openModal(event, "title")} />

                    <ActionItem icon={CalendarMonth} label="Fälligkeit" onPress={() => modalRef.current?.openModal(event, "dueDate")} />

                    <ActionItem
                      icon={Refresh}
                      onPress={() => modalRef.current?.openModal(event, "copyRule")}
                      color={!!event.copyRuleDurationUnit ? "blue" : undefined}
                      label="Wiederholung"
                    />

                    <ActionItem icon={Person} label="Zuständigkeit" onPress={() => modalRef.current?.openModal(event, "assignee")} />
                  </Fragment>
                ) : null}

                {fullAppRights && !event.completedAt ? (
                  <ActionItem icon={Delete} label="Löschen" color="red" onPress={() => modalRef.current?.openModal(event, "delete")} />
                ) : null}
              </Stack>
            </Box>

            {/* Comments / messages */}
            <Stack spacing={1.5}>
              {event.damageRegulationData == null ? null : (
                <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                  <Box sx={{ display: "flex", alignItems: "center", pr: 1.25, gap: 1.25 }}>
                    <Stack sx={{ p: 1.25, flex: 1 }} spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarMonth fontSize="small" />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {event.damageRegulationData.incidentDate
                            ? dayjs.utc(event.damageRegulationData.incidentDate).local().format("DD.MM.YYYY")
                            : "Unfalldatum unbekannt"}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {(event.damageRegulationData.damageType ?? "technical_defect") === "technical_defect"
                            ? "Technischer Defekt"
                            : (event.damageRegulationData.damageType ?? "technical_defect") === "casco_own_fault"
                              ? "Kasko (Eigenverschulden)"
                              : (event.damageRegulationData.damageType ?? "technical_defect") === "liability_third_party_fault"
                                ? "Haftpflicht (Fremdverschulden)"
                                : (event.damageRegulationData.damageType ?? "technical_defect") === "liability_own_fault"
                                  ? "Haftpflicht (Eigenverschulden)"
                                  : "Teilkasko"}
                        </Typography>
                      </Stack>
                    </Stack>

                    <IconButton onClick={() => modalRef.current?.openModal(event, "damages")}>
                      <Edit />
                    </IconButton>
                  </Box>

                  <Box sx={{ px: 1.25, py: 1.25, bgcolor: "action.hover" }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Typography variant="subtitle2">Kosten laut Rechnung</Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {new Intl.NumberFormat("de-DE", {
                            style: "currency",
                            currency: "EUR",
                          }).format(event.damageRegulationData.invoiceCost ?? 0)}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2">Selbstbeteiligung</Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {new Intl.NumberFormat("de-DE", {
                            style: "currency",
                            currency: "EUR",
                          }).format(event.damageRegulationData.deductible ?? 0)}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2">Erstattungsfähiger Betrag</Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {new Intl.NumberFormat("de-DE", {
                            style: "currency",
                            currency: "EUR",
                          }).format(Math.max((event.damageRegulationData.invoiceCost ?? 0) - (event.damageRegulationData.deductible ?? 0), 0))}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2">Erhaltene Erstattung</Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {new Intl.NumberFormat("de-DE", {
                            style: "currency",
                            currency: "EUR",
                          }).format(event.damageRegulationData.reimbursedAmount ?? 0)}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2">Offene Differenz</Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              Math.max((event.damageRegulationData.invoiceCost ?? 0) - (event.damageRegulationData.deductible ?? 0), 0) -
                                (event.damageRegulationData.reimbursedAmount ?? 0) >
                              0
                                ? "error.main"
                                : "text.secondary",
                          }}
                        >
                          {new Intl.NumberFormat("de-DE", {
                            style: "currency",
                            currency: "EUR",
                          }).format(
                            Math.max((event.damageRegulationData.invoiceCost ?? 0) - (event.damageRegulationData.deductible ?? 0), 0) -
                              (event.damageRegulationData.reimbursedAmount ?? 0),
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1">Kommentare</Typography>
                <Chip size="small" label={String(messageQuery.data?.length ?? 0)} />
              </Stack>

              {(messageQuery.data ?? []).length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Keine Kommentare vorhanden.
                </Typography>
              ) : (
                (messageQuery.data ?? []).map((message, i) => (
                  <Paper key={message.id} variant="outlined" sx={{ overflow: "hidden" }}>
                    <Box sx={{ display: "flex", alignItems: "center", pr: 1.25, gap: 1.25 }}>
                      <Stack sx={{ p: 1.25, flex: 1 }} spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Schedule fontSize="small" />
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {dayjs.utc(message.createdAt).local().fromNow()}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Person fontSize="small" />
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {message.createdBy.firstName + " " + message.createdBy.name}
                          </Typography>
                        </Stack>

                        {message.runningHours ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <AccessTime fontSize="small" />
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              {message.runningHours.runningHours + " h"}
                            </Typography>
                          </Stack>
                        ) : null}
                      </Stack>
                      {canDeleteMessage ? <IconButton aria-label="Kommentar löschen" title="Kommentar löschen"
                        disabled={messageDeleteMutation.isPending}
                        onClick={() => {
                          if (deletionInFlight.current) return;
                          setDeleteError(null);
                          setDeleteTarget({ messageId: message.id, eventId: id, deviceNumber: String(deviceNumber ?? ""),
                            preview: typeof message.message === "string" ? message.message : "Prüfprotokoll" });
                        }}>
                        <Delete size={18} />
                      </IconButton> : null}
                      {!fpmMode ? null : messageQuery.isFetching || messageLockMutation.isPending ? (
                        <CircularProgress size={"2rem"} />
                      ) : (
                        <IconButton onClick={() => messageLockMutation.mutate({ id: message.id, fpmPrivate: !message.fpmPrivate })}>
                          {message.fpmPrivate ? <Lock /> : <Public />}
                        </IconButton>
                      )}
                    </Box>

                    {/* Body */}
                    {message.format === "text" ? (
                      <Box sx={{ px: 1.25, py: 1.25, bgcolor: "action.hover" }}>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {String(message.message ?? "")}
                        </Typography>
                      </Box>
                    ) : (
                      // testing_json: keep minimal, show blocks with failed/ok/unknown semantics
                      <Box sx={{ px: 1.25, py: 1.25, bgcolor: "action.hover" }}>
                        {(Array.isArray(message.message) ? message.message : []).map((block: any, blockIndex: number) => (
                          <Stack data-ticket-label={block.failed ? "Fehler:" : "OK:"} key={`${block.title}-${blockIndex}`} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: block.failed ? "error.main" : "success.main",
                              }}
                            />
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              {block.title}
                              {block.failed ? ": " + block.comment : ""}
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    )}

                    {/* Attachments */}
                    {Array.isArray(message.attachments) && message.attachments.length > 0 ? (
                      <Box>
                        <Divider />
                        <Stack spacing={0} sx={{ p: 1 }}>
                          {message.attachments.map((fn: any, attachmentIndex: number) => (
                            <Button
                              data-ticket-attachment
                              data-ticket-file={String(fn.file)}
                              data-ticket-message={message.id}
                              data-ticket-index={attachmentIndex}
                              key={fn.file}
                              variant="text"
                              onClick={() => window.open(imageUrl(String(fn.file)), "_blank")}
                              sx={{ justifyContent: "flex-start", textTransform: "none" }}
                              startIcon={<AttachFile fontSize="small" />}
                              endIcon={<Launch fontSize="small" />}
                            >
                              <Typography variant="body2" noWrap sx={{ maxWidth: "100%" }}>
                                {fn.originalFilename ?? fn.fileName ?? fn.file}
                              </Typography>
                            </Button>
                          ))}
                        </Stack>
                      </Box>
                    ) : null}
                  </Paper>
                ))
              )}
            </Stack>
          </Stack>
        )}
      </Box>
      <Dialog open={canDeleteMessage && deleteTarget?.eventId === id} maxWidth="sm" fullWidth
        onClose={() => { if (!deletionInFlight.current) { setDeleteTarget(null); setDeleteError(null); } }}>
        <DialogTitle>Kommentar löschen</DialogTitle>
        <DialogContent>
          <Typography>Diesen einzelnen Kommentar löschen?</Typography>
          <Typography variant="body2" sx={{ mt: 2, whiteSpace: "pre-wrap" }}>{deleteTarget?.preview}</Typography>
          {deleteError ? <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={messageDeleteMutation.isPending} onClick={() => { setDeleteTarget(null); setDeleteError(null); }}>Abbrechen</Button>
          <Button color="error" variant="contained" disabled={messageDeleteMutation.isPending}
            onClick={() => {
              if (!deleteTarget || deleteTarget.eventId !== id || !canDeleteMessage || deletionInFlight.current) return;
              deletionInFlight.current = true;
              setDeleteError(null);
              messageDeleteMutation.mutate({ messageId: deleteTarget.messageId, eventId: deleteTarget.eventId, deviceNumber: deleteTarget.deviceNumber });
            }}>Löschen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
